import { useState } from "react";
import { Loader2, Camera, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const CHECKLIST_ITEMS = [
  { key: "filter_change", label: "Filter change" },
  { key: "coil_inspection", label: "Coil inspection" },
  { key: "drain_line", label: "Drain line service" },
  { key: "belt_inspection", label: "Belt inspection" },
  { key: "refrigerant_check", label: "Refrigerant level check" },
  { key: "electrical_check", label: "Electrical check" },
];

export const CHECKLIST_OPTIONS = [
  { value: "ok", label: "OK", tone: "bg-green-600 text-white border-green-600" },
  { value: "attention", label: "Needs attention", tone: "bg-amber-500 text-white border-amber-500" },
  { value: "na", label: "N/A", tone: "bg-zinc-500 text-white border-zinc-500" },
];

const ChecklistRow = ({ item, value, onChange }) => (
  <div className="py-3 border-b border-zinc-100 last:border-0">
    <p className="text-sm font-bold text-brand-ink mb-2">{item.label}</p>
    <div className="grid grid-cols-3 gap-2">
      {CHECKLIST_OPTIONS.map((opt) => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
          data-testid={`checklist-${item.key}-${opt.value}`}
          className={`text-xs font-bold py-2.5 rounded-sm border transition-colors duration-150 ${value === opt.value ? opt.tone : "bg-white text-zinc-600 border-zinc-300"}`}>
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const PhotoPicker = ({ photos, setPhotos }) => {
  const addPhotos = (e) => {
    const next = [...photos, ...Array.from(e.target.files)].slice(0, 10);
    setPhotos(next);
    e.target.value = "";
  };
  return (
    <div>
      <p className="text-sm font-bold text-brand-ink mb-2">Photos ({photos.length}/10)</p>
      <label data-testid="visit-photos-label" className="flex items-center justify-center gap-2 border-2 border-dashed border-zinc-300 hover:border-brand rounded-sm py-4 cursor-pointer text-sm font-medium text-zinc-600 transition-colors duration-200">
        <Camera className="w-5 h-5 text-brand" /> Take or add photos
        <input data-testid="visit-photos-input" type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
      </label>
      {photos.length > 0 && (
        <ul className="mt-2 space-y-1">
          {photos.map((p, i) => (
            <li key={`${p.name}-${i}`} className="flex items-center justify-between text-xs text-zinc-600 bg-white border border-zinc-200 rounded-sm px-3 py-2">
              <span className="truncate">{p.name}</span>
              <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                data-testid={`visit-photo-remove-${i}`} className="text-brand-red ml-2" aria-label="Remove photo">
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const initialChecklist = () => Object.fromEntries(CHECKLIST_ITEMS.map((i) => [i.key, ""]));

export const VisitReportForm = ({ customer, location, onDone }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [checklist, setChecklist] = useState(initialChecklist);
  const [notes, setNotes] = useState("");
  const [repairs, setRepairs] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (Object.values(checklist).some((v) => !v)) {
      toast.error("Please mark every checklist item (OK, Needs attention or N/A)");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("customer_id", customer.id);
    fd.append("location_id", location?.id || "");
    fd.append("date", date);
    fd.append("notes", notes);
    fd.append("recommended_repairs", repairs);
    Object.entries(checklist).forEach(([k, v]) => fd.append(k, v));
    photos.forEach((p) => fd.append("photos", p));
    try {
      await api.post("/tech/visit-reports", fd);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div data-testid="visit-report-success" className="bg-white border border-zinc-200 rounded-sm p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h2 className="font-heading font-bold text-xl text-brand-ink">Report submitted</h2>
        <p className="text-sm text-zinc-600 mt-2">The office has been notified and will review the report before it goes to the customer.</p>
        <button type="button" onClick={onDone} data-testid="visit-report-new-btn"
          className="mt-5 bg-brand hover:bg-brand-dark text-white font-bold px-6 py-3 rounded-sm transition-colors duration-200">
          Start Another Report
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="visit-report-form" className="bg-white border border-zinc-200 rounded-sm p-5">
      <h2 className="font-heading font-bold text-lg text-brand-ink">Visit Report</h2>
      <p className="text-sm text-zinc-600 mt-1">
        {customer.name}{location ? ` — ${location.label}, ${location.address}` : ""}
      </p>
      <div className="mt-4">
        <p className="text-sm font-bold text-brand-ink mb-1">Visit date</p>
        <Input data-testid="visit-date-input" type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm h-11" />
      </div>
      <div className="mt-4">
        {CHECKLIST_ITEMS.map((item) => (
          <ChecklistRow key={item.key} item={item} value={checklist[item.key]}
            onChange={(v) => setChecklist({ ...checklist, [item.key]: v })} />
        ))}
      </div>
      <div className="mt-4">
        <p className="text-sm font-bold text-brand-ink mb-1">Notes / assessment</p>
        <Textarea data-testid="visit-notes-input" required rows={4} placeholder="What you found, what you did, readings..."
          value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" />
      </div>
      <div className="mt-4">
        <p className="text-sm font-bold text-brand-ink mb-1">Recommended repairs (optional)</p>
        <Textarea data-testid="visit-repairs-input" rows={3} placeholder="Any repairs or follow-ups you recommend..."
          value={repairs} onChange={(e) => setRepairs(e.target.value)} className="rounded-sm" />
      </div>
      <div className="mt-4">
        <PhotoPicker photos={photos} setPhotos={setPhotos} />
      </div>
      <button type="submit" disabled={saving} data-testid="visit-report-submit-btn"
        className="mt-6 w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-6 py-4 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit Visit Report
      </button>
    </form>
  );
};
